import { useState, useMemo, Dispatch, SetStateAction, useEffect } from 'react'
import { useAppSelector } from '@store/hooks'
import { IFolder, IFile } from '@dataTypes/folder.types'
import { Patient } from '@dataTypes/PatientInfo.types'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'

export interface ISelectedFile {
  file_id: string
  study_uid: string | null
  series_uid: string | null
}

export const useImageViewerState = (): {
  selectedFiles: ISelectedFile[]
  view: 'grid' | 'list'
  searchTerm: string
  favoritesOnly: boolean
  searchFilters: FilterSelection
  setSelectedFiles: Dispatch<SetStateAction<ISelectedFile[]>>
  setView: Dispatch<SetStateAction<'grid' | 'list'>>
  setSearchTerm: Dispatch<SetStateAction<string>>
  setFavoritesOnly: Dispatch<SetStateAction<boolean>>
  setSearchFilters: Dispatch<SetStateAction<FilterSelection>>
  selectedPatient: Patient | null
  selectedFolder: IFolder | null
  childFolders: IFolder[]
  files: IFile[]
  loading: boolean
  error: string | null
  successMessage: string | null
  currentFolderName: string
  fileCount: number
  hasSelectedFiles: boolean
  refresh: number
} => {
  // Local state
  const [selectedFiles, setSelectedFiles] = useState<ISelectedFile[]>([])
  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [favoritesOnly, setFavoritesOnly] = useState<boolean>(false)
  const [searchFilters, setSearchFilters] = useState<FilterSelection>({})

  // Redux state
  const { selectedPatient, refresh } = useAppSelector((state) => state.patient)
  const {
    selectedFolder,
    childFolders,
    files,
    loading,
    error,
    successMessage,
  } = useAppSelector((state) => state.folder)

  // Reset selected files and filters when folder changes
  useEffect(() => {
    setSelectedFiles([])
    setSearchFilters({})
  }, [selectedFolder])

  // Memoized computed values
  const currentFiles = useMemo(() => {
    if (!selectedFolder) return []
    if (!files) return [] // Add safety check for undefined files
    return files.filter((file: IFile) => {
      // Apply search filter
      if (
        searchTerm &&
        !file.file_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !file.description?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false
      }
      // Apply favorites filter
      if (favoritesOnly && !file.is_favorite) {
        return false
      }
      // Apply type filter
      if (searchFilters.type && searchFilters.type.length > 0) {
        if (!searchFilters.type.includes(file.file_type || '')) {
          return false
        }
      }
      return true
    })
  }, [selectedFolder, files, searchTerm, favoritesOnly, searchFilters])

  const currentFolderName = useMemo(() => {
    return selectedFolder?.folder_name || 'Root'
  }, [selectedFolder])

  const fileCount = useMemo(() => currentFiles.length, [currentFiles])

  const hasSelectedFiles = useMemo(
    () => selectedFiles.length > 0,
    [selectedFiles],
  )

  return {
    // Local state
    selectedFiles,
    view,
    searchTerm,
    favoritesOnly,
    searchFilters,
    setSelectedFiles,
    setView,
    setSearchTerm,
    setFavoritesOnly,
    setSearchFilters,

    // Redux state
    selectedPatient,
    selectedFolder,
    childFolders,
    files: currentFiles,
    loading,
    error,
    successMessage,

    // Computed values
    currentFolderName,
    fileCount,
    hasSelectedFiles,
    refresh,
  }
}
